import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import {
  listPlanningCycles,
  listProfiledVendors,
  getVendorBrandingProfile,
  type PlanningCycle,
  type ProfiledVendor,
  type ProfileScopeRow,
} from '../../api/vendorBrandingProfileApi.js';
import {
  createWorkOrder,
  submitWorkOrder,
  listAllotted,
  listLineAttributes,
  type AllottedRow,
  type BrandingAttribute,
} from '../../api/brandingWorkOrderApi.js';
import {
  useWorkOrderBudget,
  WorkOrderBudgetWarning,
  workOrderSubmittedMessage,
} from './WorkOrderBudgetWarning.js';
import {
  brandingLineProblemsForPlan,
  brandingLineTotalAmount,
  brandingPlanForUom,
  brandingRepeatedPlace,
  type BrandingLineDimensions,
  type BrandingMeasurementPlan,
} from '../../../../shared/contracts/branding-line.js';
import { GeographyCascade, EMPTY_GEO, type GeoSelection } from './GeographyCascade.js';
import { listBrandingItems } from '../../api/brandingMasterApi.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface DraftLine {
  key: string;
  brandingTypeId: string;
  brandingItemId: string;
  otherDetail: string;
  geo: GeoSelection;
  quantity: string;
  /** Slot length, asked only for broadcast work. */
  dim3: string;
  dim3UomCode: string;
  unitOfMeasure: string;
  rateValue: string;
  /** Answers to the sub-type's own attributes, keyed by attribute id. */
  attrs: Record<string, string>;
  hsnSacCode: string;
}

const newLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  brandingTypeId: '',
  brandingItemId: '',
  otherDetail: '',
  geo: EMPTY_GEO,
  quantity: '',
  dim3: '',
  dim3UomCode: 'SECOND',
  unitOfMeasure: 'SQ_FT',
  rateValue: '',
  attrs: {},
  hsnSacCode: '',
});

/**
 * The numbers a line is priced from, in the shape the shared rules expect.
 * Area work is ordered by its total finished area, so the quantity is that
 * total and height and width stay at 1 (owner, 23 Oct).
 */
function dimsOf(l: DraftLine, plan: BrandingMeasurementPlan): BrandingLineDimensions {
  return {
    quantity: Number(l.quantity) || 0,
    dim1: 1,
    dim2: 1,
    dim3: plan.dim3 === 'DURATION' ? Number(l.dim3) || 0 : null,
    rateValue: Number(l.rateValue) || 0,
    // The order agrees a rate and nothing more; anything extra is decided on
    // the bill, and only where the order permitted it.
    additionalAmount: 0,
  };
}

/**
 * Issue a branding work order.
 *
 * A line is described by exactly what Extended Configuration holds: the
 * branding type and its sub-item, narrowed to the vendor's profile for the
 * chosen financial year, so an order can never commit work the vendor was not
 * approved for. Quantity already committed in that year is shown alongside, so
 * a second order for the same work is an informed decision rather than a
 * surprise.
 */
export function BrandingWorkOrderNewPage(): ReactNode {
  const { token } = useAuth();
  const { money, decimal } = useBusinessNumber();
  const [vendors, setVendors] = useState<readonly ProfiledVendor[]>([]);
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [scope, setScope] = useState<readonly ProfileScopeRow[]>([]);
  const [allotted, setAllotted] = useState<readonly AllottedRow[]>([]);
  // The whole registry arrives once; each line narrows it by its own sub-type.
  const [attributes, setAttributes] = useState<readonly BrandingAttribute[]>([]);
  // Sub-type id to the HSN/SAC it carries, so a chosen sub-type fills the box.
  const [hsnBySubType, setHsnBySubType] = useState<ReadonlyMap<string, string>>(new Map());
  // Sub-type id to its unit, so the line is measured the way the master says.
  const [uomBySubType, setUomBySubType] = useState<ReadonlyMap<string, string>>(new Map());
  // Sub-type id to what it declares each dimension measures.
  const [planBySubType, setPlanBySubType] = useState<
    ReadonlyMap<string, BrandingMeasurementPlan>
  >(new Map());
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [otherExpensesAllowed, setOtherExpensesAllowed] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Apart from `error`, which the profile read clears whenever it succeeds.
  const [lookupError, setLookupError] = useState<string | null>(null);
  const budget = useWorkOrderBudget();

  useEffect(() => {
    let alive = true;
    Promise.all([
      listPlanningCycles(token).catch((e) => {
        if (alive) setError(`Financial years could not be loaded: ${(e as Error).message}`);
        return [] as readonly PlanningCycle[];
      }),
      listLineAttributes(token).catch((e) => {
        if (alive) setLookupError(`Line attributes could not be loaded: ${(e as Error).message}`);
        return [] as readonly BrandingAttribute[];
      }),
    ]).then(([c, a]) => {
      if (!alive) return;
      setCycles(c);
      setAttributes(a);
      if (c.length > 0) setCycleId((p) => p || c[0].planningCycleId);
    });
    listBrandingItems(token)
      .then((res) => {
        if (!alive) return;
        setHsnBySubType(
          new Map(
            res.data
              .filter((i) => i.hsnSacCode)
              .map((i) => [i.brandingItemId, i.hsnSacCode as string]),
          ),
        );
        setUomBySubType(
          new Map(
            res.data
              .filter((i) => i.uomCode)
              .map((i) => [i.brandingItemId, i.uomCode as string]),
          ),
        );
        setPlanBySubType(
          new Map(
            res.data.map((i) => [
              i.brandingItemId,
              {
                dim1: i.dim1Meaning,
                dim2: i.dim2Meaning,
                dim3: i.dim3Meaning,
              } as BrandingMeasurementPlan,
            ]),
          ),
        );
      })
      // Without the sub-types a line falls back to what its unit implies and
      // could be measured differently from the master, so this is said.
      .catch((e) => {
        if (alive) setLookupError(`Sub-types could not be loaded: ${(e as Error).message}`);
      });
    return () => { alive = false; };
  }, [token]);

  // Only a vendor profiled for the chosen year can be given work in it, so the
  // year is picked first and the list follows it.
  useEffect(() => {
    let alive = true;
    if (!cycleId) { setVendors([]); return; }
    listProfiledVendors(cycleId, token)
      .then((v) => {
        if (!alive) return;
        setVendors(v);
        setVendorId((p) => (v.some((x) => x.vendorId === p) ? p : ''));
      })
      .catch((e) => {
        if (!alive) return;
        setVendors([]);
        setLookupError(`Vendors could not be loaded: ${(e as Error).message}`);
      });
    return () => { alive = false; };
  }, [cycleId, token]);

  useEffect(() => {
    let alive = true;
    if (!vendorId || !cycleId) { setScope([]); setAllotted([]); return; }
    Promise.all([
      getVendorBrandingProfile(vendorId, cycleId, token),
      listAllotted(token, cycleId, vendorId),
    ])
      .then(([p, a]) => {
        if (!alive) return;
        setScope(p.scope);
        setAllotted(a);
        if (p.scope.length === 0) {
          setError('This vendor has no branding profile for that year. Save the profile first.');
        } else {
          setError(null);
        }
      })
      // A profile that could not be read is not a missing profile.
      .catch((e) => {
        if (!alive) return;
        setScope([]);
        setAllotted([]);
        setError((e as Error).message);
      });
    return () => { alive = false; };
  }, [vendorId, cycleId, token]);

  const types = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of scope) m.set(s.brandingTypeId, s.typeName);
    return [...m.entries()];
  }, [scope]);

  const itemsOf = (typeId: string) =>
    scope.filter((s) => s.brandingTypeId === typeId && s.brandingItemId);

  const setLine = (key: string, patch: Partial<DraftLine>) => {
    setStatus(null);
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  /** A null type or sub-type on an attribute means it applies to everything. */
  const attributesFor = (l: DraftLine) =>
    attributes.filter(
      (a) =>
        (a.brandingTypeId === null || a.brandingTypeId === l.brandingTypeId) &&
        (a.brandingItemId === null || a.brandingItemId === l.brandingItemId),
    );

  /**
   * What the chosen sub-type says it measures. A line that names no sub-type
   * -- sponsorship and the like -- falls back to what its unit implies.
   */
  const planOf = (l: DraftLine): BrandingMeasurementPlan =>
    (l.brandingItemId ? planBySubType.get(l.brandingItemId) : undefined) ??
    brandingPlanForUom(l.unitOfMeasure);

  // Priced by the same rules the server uses, so the figure on screen is the
  // figure that gets saved.
  const total = lines.reduce((s, l) => s + brandingLineTotalAmount(dimsOf(l, planOf(l))), 0);

  const firstLineProblem = lines
    .map((l, i) => {
      const problems = brandingLineProblemsForPlan(dimsOf(l, planOf(l)), planOf(l), {
        areaEntry: 'TOTAL',
      });
      return problems.length ? `Line ${i + 1}: ${problems[0]}` : null;
    })
    .find(Boolean);

  const repeated = brandingRepeatedPlace(
    lines.map((l) => ({
      brandingTypeId: l.brandingTypeId,
      brandingItemId: l.brandingItemId,
      otherDetail: l.otherDetail,
      geoLevelCode: l.geo.geoLevelCode,
      geoNodeId: l.geo.geoNodeId,
    })),
  );
  const repeatedPlaceProblem = repeated
    ? `Line ${repeated[1]} repeats the work and geography of line ${repeated[0]}. Put the total quantity for a geography on one line.`
    : null;

  // FCM/RCM must be recorded for the vendor's year before a work order can be
  // saved (EX-WO-32 on the server); said here as soon as the vendor is chosen.
  const chosenVendor = vendors.find((v) => v.vendorId === vendorId);
  const chargeBasisMissing = !!chosenVendor && !chosenVendor.taxChargeBasisCode;

  const blocker = !vendorId ? 'Select a vendor'
    : !cycleId ? 'Select a financial year'
    : scope.length === 0 ? 'This vendor has no profile for that year'
    : chargeBasisMissing ? 'FCM / RCM is not recorded for this vendor for this financial year'
    : !validFrom || !validTo ? 'Enter the validity dates'
    : validTo < validFrom ? 'Valid to must be on or after valid from'
    : lines.some((l) => !l.brandingTypeId) ? 'Every line needs a branding type'
    : (firstLineProblem ?? repeatedPlaceProblem ?? null);

  const save = async (thenSubmit: boolean) => {
    if (blocker) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    budget.clear();
    try {
      const wo = await createWorkOrder(
        {
          vendorId,
          planningCycleId: cycleId,
          validFromDate: validFrom,
          validToDate: validTo,
          otherExpensesAllowed,
          remarks: remarks || undefined,
          lines: lines.map((l) => {
            const d = dimsOf(l, planOf(l));
            return {
              brandingTypeId: l.brandingTypeId,
              brandingItemId: l.brandingItemId || undefined,
              otherDetail: l.otherDetail || undefined,
              geoLevelCode: l.geo.geoLevelCode || undefined,
              geoNodeId: l.geo.geoNodeId || undefined,
              geoNodeCode: l.geo.geoNodeCode || undefined,
              geoNodeName: l.geo.geoNodeName || undefined,
              quantity: Number(l.quantity),
              dim1: d.dim1,
              dim2: d.dim2,
              dim3: d.dim3 ?? undefined,
              dim3UomCode: d.dim3 === null ? undefined : l.dim3UomCode,
              unitOfMeasure: l.unitOfMeasure,
              rateValue: Number(l.rateValue),
              hsnSacCode: l.hsnSacCode.trim() || undefined,
              attributes: attributesFor(l)
                .filter((a) => (l.attrs[a.attributeId] ?? '').trim())
                .map((a) => {
                  const v = l.attrs[a.attributeId].trim();
                  if (a.dataType === 'LIST') return { attributeId: a.attributeId, optionId: v };
                  if (a.dataType === 'NUMBER') return { attributeId: a.attributeId, valueNumber: Number(v) };
                  if (a.dataType === 'DATE') return { attributeId: a.attributeId, valueDate: v };
                  return { attributeId: a.attributeId, valueText: v };
                }),
            };
          }),
        },
        token,
      );
      if (thenSubmit) {
        const submitted = await submitWorkOrder(wo.workOrderId, token);
        budget.record(submitted.budgetStatus);
        setStatus(workOrderSubmittedMessage(wo.woNumber));
      } else {
        // The draft carries its budget position too. It used to be cleared
        // here, so an order could be built with no budget behind it and
        // nothing said until it was sent for approval.
        budget.record(wo.budgetStatus);
        setStatus(`${wo.woNumber} saved as draft`);
      }
      setLines([newLine()]);
      try {
        setAllotted(await listAllotted(token, cycleId, vendorId));
      } catch (e) {
        setError(`The already-allotted figures could not be refreshed: ${(e as Error).message}`);
      }
    } catch (e) {
      budget.recordFailure(e);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="work-order-new">
      <h1>Issue work order</h1>
      {lookupError ? <p role="alert">{lookupError}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}
      <WorkOrderBudgetWarning warning={budget.warning} />

      <label htmlFor="wo-cycle">Financial year</label>
      <select id="wo-cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)} data-testid="field-cycle">
        <option value="">Select a year…</option>
        {cycles.map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      <label htmlFor="wo-vendor">Vendor</label>
      <select id="wo-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="field-vendor">
        <option value="">
          {!cycleId
            ? 'Pick a financial year first'
            : vendors.length === 0
              ? 'No vendor is profiled for that year'
              : 'Select a vendor…'}
        </option>
        {vendors.map((v) => (
          <option key={v.vendorId} value={v.vendorId}>{v.vendorCode} — {v.vendorName}</option>
        ))}
      </select>
      {chargeBasisMissing ? (
        <p role="alert" data-testid="wo-charge-basis-missing">
          Record forward charge (FCM) or reverse charge (RCM) for {chosenVendor!.vendorName} for
          this financial year before this work order can be saved.{' '}
          <Link to={`/admin/vendors/branding-profile?vendorId=${encodeURIComponent(vendorId)}&planningCycleId=${encodeURIComponent(cycleId)}`}>
            Open the vendor's branding profile
          </Link>
        </p>
      ) : null}

      <label htmlFor="wo-from">Valid from</label>
      <DateInput id="wo-from" type="date" value={validFrom} onValueChange={(nextValue) => setValidFrom(nextValue)} data-testid="field-valid-from" />
      <label htmlFor="wo-to">Valid to</label>
      <DateInput id="wo-to" type="date" value={validTo} onValueChange={(nextValue) => setValidTo(nextValue)} data-testid="field-valid-to" />

      {/* No amount is agreed here: this only decides whether the vendor may put
          one on the bill at all. Unticked, m13 refuses the expense outright. */}
      <label htmlFor="wo-expenses">
        <input
          id="wo-expenses"
          type="checkbox"
          checked={otherExpensesAllowed}
          onChange={(e) => setOtherExpensesAllowed(e.target.checked)}
          data-testid="field-other-expenses"
        />{' '}
        Vendor may bill additional expenses against this order
      </label>

      {allotted.length > 0 ? (
        <section aria-label="already-allotted">
          <h2>Already committed this year</h2>
          <table>
            <thead>
              <tr><th>Type</th><th>Sub-type</th><th>Quantity</th><th>Orders</th></tr>
            </thead>
            <tbody>
              {allotted.map((a, i) => (
                <tr key={`${a.typeCode}-${a.itemCode ?? 'ALL'}-${i}`} data-testid={`allotted-${i}`}>
                  <td>{a.typeName}</td>
                  <td>{a.itemName ?? '—'}</td>
                  <td>{decimal(a.allottedQuantity)} {a.unitOfMeasure}</td>
                  <td>{decimal(a.workOrderCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section aria-label="work-order-lines">
        <h2>Lines</h2>
        {lines.map((l, idx) => {
          const items = itemsOf(l.brandingTypeId);
          const typeCode = types.find(([id]) => id === l.brandingTypeId)?.[1] ?? '';
          return (
            <fieldset key={l.key} data-testid={`line-${idx}`}>
              <legend>Line {idx + 1}</legend>

              <label htmlFor={`t-${l.key}`}>Branding type</label>
              <select
                id={`t-${l.key}`}
                value={l.brandingTypeId}
                onChange={(e) => setLine(l.key, { brandingTypeId: e.target.value, brandingItemId: '' })}
                data-testid={`line-type-${idx}`}
              >
                <option value="">Select…</option>
                {types.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>

              <label htmlFor={`i-${l.key}`}>Sub-type</label>
              <select
                id={`i-${l.key}`}
                value={l.brandingItemId}
                onChange={(e) =>
                  setLine(l.key, {
                    brandingItemId: e.target.value,
                    attrs: {},
                    hsnSacCode: hsnBySubType.get(e.target.value) ?? '',
                    unitOfMeasure: uomBySubType.get(e.target.value) ?? l.unitOfMeasure,
                  })
                }
                data-testid={`line-item-${idx}`}
              >
                <option value="">
                  {items.length === 0 ? 'No sub-type in profile' : 'Select…'}
                </option>
                {items.map((s) => (
                  <option key={s.brandingItemId!} value={s.brandingItemId!}>{s.itemName}</option>
                ))}
              </select>

              {/* What the sub-type itself declares — variant, size, illumination.
                  Held in t628/t629, so a new detail is a master change, not a
                  code change. */}
              {l.brandingItemId && attributesFor(l).length > 0 ? (
                <span data-testid={`line-attributes-${idx}`}>
                  {attributesFor(l).map((a) => (
                    <span key={a.attributeId}>
                      <label htmlFor={`a-${l.key}-${a.attributeId}`}>{a.attributeName}</label>
                      {a.dataType === 'LIST' ? (
                        <select
                          id={`a-${l.key}-${a.attributeId}`}
                          value={l.attrs[a.attributeId] ?? ''}
                          onChange={(e) =>
                            setLine(l.key, { attrs: { ...l.attrs, [a.attributeId]: e.target.value } })
                          }
                          data-testid={`line-attr-${idx}-${a.attributeCode}`}
                        >
                          <option value="">Select…</option>
                          {a.options.map((o) => (
                            <option key={o.optionId} value={o.optionId}>{o.optionName}</option>
                          ))}
                        </select>
                      ) : a.dataType === 'DATE' ? (
                        <DateInput
                          id={`a-${l.key}-${a.attributeId}`}
                          value={l.attrs[a.attributeId] ?? ''}
                          onValueChange={(nextValue) =>
                            setLine(l.key, { attrs: { ...l.attrs, [a.attributeId]: nextValue } })
                          }
                          data-testid={`line-attr-${idx}-${a.attributeCode}`}
                        />
                      ) : (
                        <input
                          id={`a-${l.key}-${a.attributeId}`}
                          type={a.dataType === 'NUMBER' ? 'number' : 'text'}
                          value={l.attrs[a.attributeId] ?? ''}
                          onChange={(e) =>
                            setLine(l.key, { attrs: { ...l.attrs, [a.attributeId]: e.target.value } })
                          }
                          data-testid={`line-attr-${idx}-${a.attributeCode}`}
                        />
                      )}
                    </span>
                  ))}
                </span>
              ) : null}

              {/* Defaults from the sub-type; changed here when the material
                  decides it, and frozen on the line once the order is approved. */}
              <label htmlFor={`hsn-${l.key}`}>HSN / SAC</label>
              <input
                id={`hsn-${l.key}`}
                value={l.hsnSacCode}
                inputMode="numeric"
                maxLength={8}
                placeholder={l.brandingItemId ? 'Not set on this sub-type' : 'Pick a sub-type first'}
                onChange={(e) => setLine(l.key, { hsnSacCode: e.target.value })}
                data-testid={`line-hsn-${idx}`}
              />

              {/* Sponsorship and similar OTHERS work is described in words. */}
              {typeCode.toUpperCase().includes('OTHER') ? (
                <>
                  <label htmlFor={`o-${l.key}`}>Details</label>
                  <input
                    id={`o-${l.key}`}
                    value={l.otherDetail}
                    onChange={(e) => setLine(l.key, { otherDetail: e.target.value })}
                    placeholder="e.g. India Australia cricket match"
                    data-testid={`line-other-${idx}`}
                  />
                </>
              ) : null}

              <fieldset data-testid={`line-geo-${idx}`}>
                <legend>Coverage</legend>
                <GeographyCascade
                  idPrefix={`geo-${l.key}`}
                  testIdPrefix={`line-${idx}-geo`}
                  onChange={(geo) => setLine(l.key, { geo })}
                />
                <span data-testid={`line-geo-summary-${idx}`}>
                  {l.geo.geoNodeName || 'All India'}
                </span>
              </fieldset>

              <label htmlFor={`u-${l.key}`}>Unit</label>
              <input id={`u-${l.key}`} value={l.unitOfMeasure} onChange={(e) => setLine(l.key, { unitOfMeasure: e.target.value })} data-testid={`line-uom-${idx}`} />

              <label htmlFor={`q-${l.key}`}>Quantity</label>
              <input id={`q-${l.key}`} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} data-testid={`line-qty-${idx}`} />


              {planOf(l).dim3 === 'DURATION' ? (
                <span data-testid={`line-slot-fields-${idx}`}>
                  <label htmlFor={`s-${l.key}`}>Slot length</label>
                  <input id={`s-${l.key}`} type="number" min="0" step="1" value={l.dim3} onChange={(e) => setLine(l.key, { dim3: e.target.value })} data-testid={`line-slot-${idx}`} />
                  <select
                    aria-label="Slot unit"
                    value={l.dim3UomCode}
                    onChange={(e) => setLine(l.key, { dim3UomCode: e.target.value })}
                    data-testid={`line-slot-uom-${idx}`}
                  >
                    <option value="SECOND">Second</option>
                    <option value="DURATION">Duration</option>
                  </select>
                </span>
              ) : null}

              <label htmlFor={`r-${l.key}`}>Rate (INR)</label>
              <input id={`r-${l.key}`} type="number" min="0" step="0.01" value={l.rateValue} onChange={(e) => setLine(l.key, { rateValue: e.target.value })} data-testid={`line-rate-${idx}`} />

              <span data-testid={`line-amount-${idx}`}>
                {money(brandingLineTotalAmount(dimsOf(l, planOf(l))))}
              </span>

              {lines.length > 1 ? (
                <Button
                  type="button"
                  variant="subtle"
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  data-testid={`remove-line-${idx}`}
                >
                  Remove
                </Button>
              ) : null}
            </fieldset>
          );
        })}
        <Button type="button" variant="secondary" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="add-line">
          Add line
        </Button>
      </section>

      <p data-testid="wo-total">Total: {money(total)}</p>

      <label htmlFor="wo-remarks">Remarks</label>
      <textarea id="wo-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="field-remarks" />

      {blocker ? <p role="status" data-testid="wo-blocker">{blocker}</p> : null}
      <Button type="button" variant="secondary" onClick={() => void save(false)} disabled={!!blocker || busy} title={blocker ?? undefined} data-testid="save-draft">
        Save draft
      </Button>
      <Button type="button" variant="primary" onClick={() => void save(true)} disabled={!!blocker || busy} title={blocker ?? undefined} data-testid="save-submit">
        Save and submit for approval
      </Button>
    </main>
  );
}
